import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Tabs, Tab, Dropdown } from "react-bootstrap";
import CustomButton from "../../common-components/button/Button.jsx";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStep } from "../../state/reducers/architectureConversion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCode, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { targetTechOptions, techDetection } from "./arch-options.js"

function ArchChooseLanguage() {
  const dispatch = useDispatch();
  const selectedStructure = useSelector((state) => state.architectureConversion.selectedStructure);
  const [detectedTechnologies, setDetectedTechnologies] = useState([]);
  const [targetTechnologies, setTargetTechnologies] = useState({});
  const [activeTab, setActiveTab] = useState("techStack");

  useEffect(() => {
    if (selectedStructure) {
      detectTechnologies(selectedStructure);
    }
  }, [selectedStructure]);

  const detectTechnologies = (structure) => {
    const technologies = [];
    const fileExtensions = getAllFileExtensions(structure);
  
    techDetection.forEach(tech => {
      if (tech.exts.some(ext => fileExtensions.includes(ext))) {
        technologies.push({
          name: tech.name,
          icon: tech.icon,
          fileTypes: tech.exts.filter(ext => fileExtensions.includes(ext)),
        });
      }
    });
  
    // Check for package.json file to detect React and Angular
    const packageJsonFile = structure.find(node => node.name === "package.json");
    if (packageJsonFile && packageJsonFile.content) {
      try {
        const packageJsonContent = JSON.parse(packageJsonFile.content);
        const dependencies = {
          ...(packageJsonContent.dependencies || {}),
          ...(packageJsonContent.devDependencies || {})
        };
  
        // Check for React
        if (dependencies.react || dependencies["react-dom"]) {
          technologies.push({
            name: "React",
            icon: faCode,
            fileTypes: [".js", ".jsx", "package.json"],
          });
        }
  
        // Check for Angular
        if (dependencies["@angular/core"]) {
          technologies.push({
            name: "Angular",
            icon: faCode,
            fileTypes: [".ts", ".html", "package.json"],
          });
        }
      } catch (error) {
        console.warn("Error parsing package.json:", error);
      }
    }

    // Detect Python and PySpark files
    const pythonFiles = structure.filter(node => 
      node.type === "file" && 
      (node.extension === ".py" || node.name.endsWith(".py"))
    );

    if (pythonFiles.length > 0) {
      let hasPySpark = false;

      pythonFiles.forEach(file => {
        if (file.content) {
          // Detect PySpark by checking for common imports
          if (file.content.includes("pyspark") || file.content.includes("from pyspark")) {
            hasPySpark = true;
          }
        }
      });

      if (hasPySpark) {
        technologies.push({
          name: "PySpark",
          icon: faCode,
          fileTypes: [".py"],
        });
      }

      // Add Python if we found any Python files
      technologies.push({
        name: "Python",
        icon: faCode,
        fileTypes: [".py"],
      });
    }
  
    // Handle additional logic for HTML files and Angular
    if (fileExtensions.includes(".html") && technologies.some(t => t.name === "Angular")) {
      const angularTech = technologies.find(t => t.name === "Angular");
      if (angularTech && !angularTech.fileTypes.includes(".html")) {
        angularTech.fileTypes.push(".html");
      }
    }
  
    setDetectedTechnologies(technologies);
  };
  

  const getAllFileExtensions = (structure) => {
    const extensions = new Set();
    const traverse = (node) => {
      if (node.type === "file") {
        const extension = node.extension || "." + node.name.split(".").pop();
        extensions.add(extension);
        extensions.add(node.name); // For files like package.json, requirements.txt
      } else if (node.children) {
        node.children.forEach(traverse);
      }
    };
    
    if (Array.isArray(structure)) {
      structure.forEach(traverse);
    } else if (structure && typeof structure === "object") {
      traverse(structure);
    }
    
    return Array.from(extensions);
  };

  const handleNext = () => {
    const sourceStack = detectedTechnologies.map(tech => tech.name);
    localStorage.setItem("sourceStack", JSON.stringify(sourceStack));

    const targetStack = Object.values(targetTechnologies);
    localStorage.setItem("targetStack", JSON.stringify(targetStack));

    dispatch(setCurrentStep(5));
  };

  const handleBack = () => {
    dispatch(setCurrentStep(2));
  };

  const handleTargetTechSelect = (sourceTech, targetTech) => {
    setTargetTechnologies(prev => ({
      ...prev,
      [sourceTech]: targetTech
    }));
  };

  const renderTechStack = () => (
    <div className="tech-stack-wrapper">
      <div className="source-stack">
        <h3 className="tab-title">Source Stack</h3>
        {detectedTechnologies.length > 0 ? (
          detectedTechnologies.map((tech, index) => (
            <Card key={index} className="mb-3">
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="arch-title">
                    <FontAwesomeIcon icon={tech.icon} className="me-2" />
                    {tech.name}
                  </h5>
                  <div className="file-type">
                    <span className="me-2">File types:</span>
                    {tech.fileTypes.map((type, i) => (
                      <span key={i} className="badge ext-badge me-1">{type}</span>
                    ))}
                  </div>
                </div>
                <Dropdown>
                  <Dropdown.Toggle variant="link" id={`dropdown-${tech.name}`}>
                    <FontAwesomeIcon icon={faArrowRight} size="lg" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {targetTechOptions[tech.name].options.map((option, i) => (
                      <Dropdown.Item key={i} onClick={() => handleTargetTechSelect(tech.name, option)}>
                        {option}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </Card.Body>
            </Card>
          ))
        ) : (
          <Card className="mb-3">
            <Card.Body>
              <p className="text-muted">No source technologies detected. Please select a project structure first.</p>
            </Card.Body>
          </Card>
        )}
      </div>
      <div className="gap-stack"></div>
      <div className="target-stack">
        <h3 className="tab-title">Target Stack</h3>
        {detectedTechnologies.length > 0 ? (
          Object.entries(targetTechnologies).length > 0 ? (
            Object.entries(targetTechnologies).map(([sourceTech, targetTech], index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <h5 className="arch-title">
                    <FontAwesomeIcon icon={faCode} className="me-2" />
                    {targetTech} <span className="converting-txt">Converting from: {sourceTech}</span>
                  </h5>
                  
                  <div className="file-type">
                    <span className="me-2">File types:</span>
                    {targetTechOptions[sourceTech].fileTypes[targetTech].map((type, i) => (
                      <span key={i} className="badge ext-badge me-1">{type}</span>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            ))
          ) : (
            <Card className="mb-3">
              <Card.Body>
                <p className="text-muted">No target technologies selected. Please choose target technologies from the Source Stack.</p>
              </Card.Body>
            </Card>
          )
        ) : (
          <Card className="mb-3">
            <Card.Body>
              <p className="text-muted">Target stack will be available once source technologies are detected.</p>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  );

  return (
    <div className="page-wrapper database-conversion-page px-3">
      <Container>
        <Row>
          <Col>
            <h2 className="page-title">Choose Target Architecture/Platform</h2>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="tabs-title"
            >
              <Tab eventKey="techStack" title="Tech Stack">
                {renderTechStack()}
              </Tab>
            </Tabs>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col className="text-end">
            <CustomButton className="me-3" onClick={handleBack} label="Back" />
            <CustomButton onClick={handleNext} label="Next" />
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default ArchChooseLanguage;